import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";

export interface InternalCalendar {
  id: string;
  name: string;
  color: string | null;
  isDefault: boolean;
  isVisible: boolean;
  sortOrder: number;
  isPlanningOnly?: boolean;
}

export const INTERNAL_CALENDARS_QUERY_KEY = ["/api/calendar/internal-calendars"] as const;

export function AdditionalCalendarsContent({ instance }: { instance: "desktop" | "mobile" }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [newIsPlanningOnly, setNewIsPlanningOnly] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingIsPlanningOnly, setEditingIsPlanningOnly] = useState(false);

  const { data: calendars = [], isLoading } = useQuery<InternalCalendar[]>({
    queryKey: INTERNAL_CALENDARS_QUERY_KEY,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: INTERNAL_CALENDARS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
  };

  const createMutation = useMutation({
    mutationFn: (data: { name: string; color?: string; isPlanningOnly: boolean }) =>
      apiRequest("POST", "/api/calendar/internal-calendars", data),
    onSuccess: () => {
      setNewName("");
      setNewIsPlanningOnly(false);
      refresh();
    },
    onError: (error: Error) =>
      toast({ title: "Couldn't create calendar", description: error.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Pick<InternalCalendar, "name" | "color" | "isVisible" | "sortOrder" | "isPlanningOnly">> }) =>
      apiRequest("PATCH", `/api/calendar/internal-calendars/${id}`, updates),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: (error: Error) => {
      refresh();
      toast({ title: "Couldn't update calendar", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/calendar/internal-calendars/${id}`),
    onSuccess: refresh,
    onError: (error: Error) =>
      toast({ title: "Couldn't delete calendar", description: error.message, variant: "destructive" }),
  });

  const sortedCalendars = [...calendars].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-3" data-testid={`panel-additional-calendars-${instance}`}>
      <div>
        <h2 className="font-semibold">Additional Calendars</h2>
        <p className="text-xs text-muted-foreground">Choose which GoalVia calendars are shown.</p>
      </div>

      <div className="space-y-2" aria-busy={isLoading}>
        {isLoading && <p className="text-sm text-muted-foreground">Loading calendars…</p>}
        {!isLoading && sortedCalendars.length === 0 && (
          <p className="text-sm text-muted-foreground">No calendars yet.</p>
        )}
        {sortedCalendars.map((calendar) => (
          <div
            key={calendar.id}
            className="flex items-center gap-2 rounded-md px-1 py-1.5"
            data-testid={`row-internal-calendar-${instance}-${calendar.id}`}
          >
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: calendar.color || "#3b82f6" }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-sm">
              {calendar.name}
              {calendar.isDefault && <span className="sr-only"> (default)</span>}
            </span>
            {calendar.isPlanningOnly && (
              <Badge variant="secondary" className="shrink-0 text-[10px]" data-testid={`badge-planning-calendar-${instance}-${calendar.id}`}>
                Planning only
              </Badge>
            )}
            <Switch
              checked={calendar.isVisible}
              disabled={updateMutation.isPending}
              aria-label={`${calendar.isVisible ? "Hide" : "Show"} ${calendar.name}`}
              onCheckedChange={(isVisible) =>
                updateMutation.mutate({ id: calendar.id, updates: { isVisible } })
              }
              data-testid={`switch-internal-calendar-${instance}-${calendar.id}`}
            />
            {!calendar.isDefault && (
              <Popover
                onOpenChange={(open) => {
                  if (open) {
                    setEditingId(calendar.id);
                    setEditingName(calendar.name);
                    setEditingIsPlanningOnly(calendar.isPlanningOnly === true);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Manage ${calendar.name}`}
                    data-testid={`button-manage-calendar-${instance}-${calendar.id}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 space-y-3" align="end">
                  <label className="space-y-1 text-sm">
                    <span>Name</span>
                    <Input
                      value={editingId === calendar.id ? editingName : calendar.name}
                      onFocus={() => {
                        setEditingId(calendar.id);
                        setEditingName(calendar.name);
                      }}
                      onChange={(event) => {
                        setEditingId(calendar.id);
                        setEditingName(event.target.value);
                      }}
                      data-testid={`input-rename-calendar-${instance}-${calendar.id}`}
                    />
                  </label>
                  <label className="flex items-center justify-between text-sm">
                    <span>Color</span>
                    <Input
                      type="color"
                      className="h-9 w-14 p-1"
                      value={calendar.color || "#3b82f6"}
                      onChange={(event) =>
                        updateMutation.mutate({ id: calendar.id, updates: { color: event.target.value } })
                      }
                      data-testid={`input-calendar-color-${instance}-${calendar.id}`}
                    />
                  </label>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm" htmlFor={`planning-calendar-${instance}-${calendar.id}`}>
                      <Checkbox
                        id={`planning-calendar-${instance}-${calendar.id}`}
                        checked={editingIsPlanningOnly}
                        onCheckedChange={(checked) => setEditingIsPlanningOnly(checked === true)}
                        data-testid={`checkbox-planning-calendar-${instance}-${calendar.id}`}
                      />
                      For Planning Only
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Events stay in GoalVia and do not sync to connected calendars.
                    </p>
                  </div>
                  <div className="flex justify-between gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Delete "${calendar.name}"? Its events will move to your default calendar.`)) {
                          deleteMutation.mutate(calendar.id);
                        }
                      }}
                      data-testid={`button-delete-calendar-${instance}-${calendar.id}`}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      disabled={!editingName.trim() || updateMutation.isPending}
                      onClick={() =>
                        updateMutation.mutate({
                          id: calendar.id,
                          updates: { name: editingName.trim(), isPlanningOnly: editingIsPlanningOnly },
                        })
                      }
                      data-testid={`button-save-calendar-${instance}-${calendar.id}`}
                    >
                      Save
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        ))}
      </div>

      <form
        className="space-y-2 border-t pt-3"
        onSubmit={(event) => {
          event.preventDefault();
          const name = newName.trim();
          if (name) createMutation.mutate({ name, color: newColor, isPlanningOnly: newIsPlanningOnly });
        }}
      >
        <Input
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="New calendar name"
          aria-label="New calendar name"
          data-testid={`input-new-calendar-${instance}`}
        />
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm" htmlFor={`new-planning-calendar-${instance}`}>
            <Checkbox
              id={`new-planning-calendar-${instance}`}
              checked={newIsPlanningOnly}
              onCheckedChange={(checked) => setNewIsPlanningOnly(checked === true)}
              data-testid={`checkbox-new-planning-calendar-${instance}`}
            />
            For Planning Only
          </label>
          <p className="text-xs text-muted-foreground">
            Events stay in GoalVia and do not sync to connected calendars.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            type="color"
            className="h-9 w-12 p-1"
            value={newColor}
            onChange={(event) => setNewColor(event.target.value)}
            aria-label="New calendar color"
            data-testid={`input-new-calendar-color-${instance}`}
          />
          <Button
            type="submit"
            size="sm"
            className="flex-1"
            disabled={!newName.trim() || createMutation.isPending}
            data-testid={`button-create-calendar-${instance}`}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add calendar
          </Button>
        </div>
      </form>
    </div>
  );
}